import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { Container } from "@mui/material";
import { Routes, Route, Navigate } from "react-router-dom";
import LookupDomain, { PrefetchLookupDomain } from "./components/LookupDomain";
import LookupCompany from "./components/LookupCompany";
import BusinessAttributes from "./components/BusinessAttributes";
import JSONResponse from "./components/JSONResponse";
import PageHeader from "./components/PageHeader";
import SearchHistory from "./components/SearchHistory";
import BlockedDomains from "./components/BlockedDomains";
import MonitoringPortfolio from "./components/Monitoring";
import MonitoringAlerts from "./components/Monitoring/MonitoringAlerts";
import MatchProfiles from "./components/MatchProfile";
import MatchProfileForm from "./components/MatchProfile/MatchProfileForm";
import Passfort from "./components/Passfort";
import Relay from "./components/Relay";
import Admin from "./components/Admin";
import DataExports from "./components/DataExports";
import { Authenticated, Anonymous } from "./components/IdentityProvider";
import ActiveMonitorsDrawerV3 from "./components/Monitoring/ActiveMonitorsDrawer";

import "./App.css";
import DomainFinder from "./components/DomainFinder";
import WebsiteStatusCheck from "./components/WebsiteStatusCheck";
import SearchParameters from "./components/SearchParameters";
import Oops from "./components/ErrorBoundary/Oops";

import TKHomeBusinessAttributes from "./components/BusinessAttributes/TKHome";
import useAdminSettings from "./utilities/adminSettings";
import RiskDefinitions from "./components/RiskDefinitions";

export default function App() {
  const [settings] = useAdminSettings();

  const responseDisplayRoutes = (
    <>
      <Route path="" element={<BusinessAttributes />} />
      <Route path="parameters" element={<SearchParameters />} />
      <Route path="json" element={<JSONResponse />} />
    </>
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <PrefetchLookupDomain />

      <ActiveMonitorsDrawerV3>
        <DomainFinder>
          <WebsiteStatusCheck>
            <Routes>
              <Route index element={Authenticated(<LookupDomain />)} />
              <Route
                path="/domain"
                element={Authenticated(<Navigate to="/" replace />)}
              />
              <Route
                path="/domain/:domain"
                element={Authenticated(<LookupDomain />)}
              />

              <Route
                path="/blocked"
                element={Authenticated(
                  <>
                    <PageHeader invert />
                    <Container maxWidth="xl">
                      <BlockedDomains />
                    </Container>
                  </>
                )}
              />

              <Route
                path="/searches"
                element={Authenticated(
                  <>
                    <PageHeader invert />
                    <Container maxWidth="xl">
                      <SearchHistory />
                    </Container>
                  </>
                )}
              />

              <Route
                path="/search/:domain/:searchId"
                element={Authenticated(
                  <>
                    <PageHeader invert />
                    <Container maxWidth="xl">
                      <LookupCompany />
                    </Container>
                  </>
                )}
              >
                {responseDisplayRoutes}
              </Route>

              {settings.allowTKHomeMock === true && (
                <Route
                  path="/portfolio/tkhome.ca"
                  element={Authenticated(
                    <>
                      <PageHeader invert />
                      <Container maxWidth="xl">
                        <LookupCompany />
                        <TKHomeBusinessAttributes />
                      </Container>
                    </>
                  )}
                />
              )}

              {settings.allowTKHomeMock === true && (
                <Route
                  path="/portfolio/tkhome.ca/latest"
                  element={Authenticated(
                    <>
                      <PageHeader invert />
                      <Container maxWidth="xl">
                        <LookupCompany />
                        <TKHomeBusinessAttributes after />
                      </Container>
                    </>
                  )}
                />
              )}

              {/* This route is for stable alert urls in the public monitoring api */}
              {/* it must come before the catch-all defined below */}
              <Route
                path="/monitoring/alerts/:alertId"
                element={Authenticated(
                  <>
                    <PageHeader invert />
                    <Container maxWidth="xl">
                      <MonitoringAlerts />
                    </Container>
                  </>
                )}
              />

              <Route
                path="/monitoring/*"
                element={Authenticated(
                  <>
                    <PageHeader invert />
                    <Container maxWidth="xl">
                      <MonitoringPortfolio />
                    </Container>
                  </>
                )}
              />

              <Route
                path="/risk_profiles"
                element={Authenticated(
                  <>
                    <PageHeader invert />
                    <Container maxWidth="xl">
                      <MatchProfiles />
                    </Container>
                  </>
                )}
              />

              <Route
                path="/risk_profile/create"
                element={Authenticated(
                  <>
                    <PageHeader invert />
                    <Container maxWidth="xl">
                      <MatchProfileForm readOnly={false} />
                    </Container>
                  </>
                )}
              />

              <Route
                path="/risk_profile/view/:matchProfileId"
                element={Authenticated(
                  <>
                    <PageHeader invert />
                    <Container maxWidth="xl">
                      <MatchProfileForm readOnly={true} />
                    </Container>
                  </>
                )}
              />

              <Route
                path="/risk_profile/edit/:matchProfileId"
                element={Authenticated(
                  <>
                    <PageHeader invert />
                    <Container maxWidth="xl">
                      <MatchProfileForm readOnly={false} />
                    </Container>
                  </>
                )}
              />

              <Route
                path="/passfort/search/:searchId"
                element={Anonymous(
                  <>
                    <PageHeader
                      logo
                      divider
                      navLinks={false}
                      hamburgerMenu={false}
                      invert
                    />
                    <Container maxWidth="xl">
                      <Passfort />
                    </Container>
                  </>
                )}
              >
                {responseDisplayRoutes}
              </Route>

              <Route
                path="/relay/search/:searchId"
                element={Anonymous(
                  <>
                    <PageHeader
                      logo
                      divider
                      navLinks={false}
                      hamburgerMenu={false}
                      invert
                    />
                    <Container maxWidth="xl">
                      <Relay />
                    </Container>
                  </>
                )}
              >
                {responseDisplayRoutes}
              </Route>

              <Route
                path="/exports"
                element={Authenticated(
                  <>
                    <PageHeader />
                    <Container maxWidth="xl">
                      <DataExports />
                    </Container>
                  </>
                )}
              />

              <Route
                path="/risk_definitions"
                element={Authenticated(
                  <>
                    <PageHeader />
                    <Container maxWidth="xl">
                      <RiskDefinitions />
                    </Container>
                  </>
                )}
              />

              <Route
                path="/qqqadmin"
                element={Authenticated(
                  <>
                    <PageHeader />
                    <Container maxWidth="xl">
                      <Admin />
                    </Container>
                  </>
                )}
              />

              <Route
                path="*"
                element={
                  <Oops message="We couldn’t find the page you were looking for." />
                }
              />
            </Routes>
          </WebsiteStatusCheck>
        </DomainFinder>
      </ActiveMonitorsDrawerV3>
    </LocalizationProvider>
  );
}
